import { forwardRef } from 'react';

import { cx } from 'cva';
import {
  CheckboxGroup as RACCheckboxGroup,
  type CheckboxGroupProps as RACCheckboxGroupProps,
  type ValidationResult,
} from 'react-aria-components/CheckboxGroup';

import { FieldDescription } from '../form/field_description';
import { FieldError } from '../form/field_error';
import { FieldLabel } from '../form/field_label';

import styles from './checkbox_group.module.css';

export type CheckboxGroupProps = RACCheckboxGroupProps & {
  children: React.ReactNode;
  defaultValue?: string[];
  description?: string;
  errorMessage?: string | ((validation: ValidationResult) => string);
  label?: string;
  tooltip?: React.ReactNode;
  value?: string[];
};
/**
 * Wrapper for CheckboxGroup component.
 *
 * <CheckboxGroup label="Label" description="Description" />

* See also https://react-spectrum.adobe.com/react-aria/CheckboxGroup.html
 *
 */

export const CheckboxGroup = forwardRef<HTMLDivElement, CheckboxGroupProps>(
  (
    {
      label,
      description,
      errorMessage,
      className,
      children,
      isRequired,
      tooltip,
      ...props
    },
    ref,
  ) => {
    const classes = cx(styles.checkbox_group, className);

    return (
      <RACCheckboxGroup
        {...props}
        ref={ref}
        className={classes}
        isReadOnly={props.isReadOnly}
        isRequired={isRequired}
      >
        {label && (
          <FieldLabel isRequired={isRequired} tooltip={tooltip}>
            {label}
          </FieldLabel>
        )}
        {children}
        {description && <FieldDescription>{description}</FieldDescription>}
        <FieldError>{errorMessage}</FieldError>
      </RACCheckboxGroup>
    );
  },
);
